import React, { forwardRef, useState, useEffect } from 'react';
import { InputNumber as AntInputNumber } from 'antd';
import { MathPlus, MathMinus, ErrorFilled } from '@nackle/origami-icons';
import classNames from 'classnames';
import { Button } from '../../exports';

/**
 * InputNumber component that wraps Ant Design's InputNumber with additional functionality.
 * Provides a numeric input field with increment/decrement buttons and validation.
 *
 * {@link https://ant.design/components/input-number#api}
 * 
 * @param {Object} props - The component props
 * @param {boolean} [props.disableMax] - disable the plus button when it hits max value
 * @param {number} [props.value] - Current value of the input
 * @param {string} [props.size] - Size of the input ('small', 'medium', 'large')
 * @param {boolean} [props.error] - Whether the input has an error state
 * @param {number} [props.min] - Minimum allowed value
 * @param {number} [props.max] - Maximum allowed value
 * @param {number} [props.step=1] - Step increment/decrement value
 * @param {boolean} [props.optional] - Whether the field is optional
 * @param {number} [props.defaultValue] - Default value when uncontrolled
 * @param {boolean} [props.disabled] - Whether the input is disabled
 * @param {Function} [props.onStep] - Callback when step buttons are clicked (value, stepInfo)
 * @param {Function} [props.onChange] - Callback when input value changes
 * @param {Function} [props.onKeyUp] - Callback for keyup events
 * @param {boolean} [props.preventNegative] - Whether to prevent negative values
 * @param {React.Ref} ref - Forwarded ref to the input element
 * @returns {React.ReactElement} An InputNumber component
 */
const InputNumber = forwardRef( ( props, ref ) => {
    const {
        value,
        size,
        error,
        min,
        max,
        step,
        optional,
        defaultValue,
        disabled,
        onStep,
        onChange,
        onKeyUp,
        preventNegative,
        disableMax,
        ...restProps
     } = props;
    const [ number, setNumber ] = useState( defaultValue || undefined );
    useEffect( () => {
        if ( min && number === undefined ) {
            setNumber( min );
        }
    }, [ min ] );
    useEffect( () => {
        if ( number !== value ) {
            setNumber( value );
        }
    }, [ value ] );
    useEffect( ()=> {
        const inputs = document.querySelectorAll( '.nkl-input input' ); 
        inputs.forEach( input => {
            input.addEventListener( 'mousewheel', function( evt ) { evt.preventDefault(); } );
            return ()=> input.removeEventListener( 'mousewheel' );
        } );
    }, [] );
    /**
     * Gets the appropriate CSS class name based on the size prop
     * 
     * @param {string} size - Size of the input ('small', 'medium', 'large')
     * @returns {string} CSS class name for the specified size
     */
    const getSizeClassName = ( size ) => {
        if ( size === 'large' ) {
            return 'origami-input-number-large';
        }
        if ( size === 'small' ) {
            return 'origami-input-number-small';
        } else {
            return '';
        }
    };

    /**
     * Determines if the input should have an error class based on validation rules
     * 
     * @param {boolean} error - Whether the input has an explicit error state
     * @param {number} min - Minimum allowed value
     * @param {number} max - Maximum allowed value
     * @param {boolean} optional - Whether the field is optional
     * @returns {string|boolean} Error class name or false if no error
     */
    const getErrorClassName = ( error, min, max, optional ) => {
        if ( number === 0 && optional === true ) {
            return false;
        }
        if ( number > max || number < min || error ) {
            return 'origami-input-number-error';
        }
    };

    const sizeClassName = getSizeClassName( size );
    const errorClassName = getErrorClassName( error, min, max, optional );

    const inputClass = classNames( {
        'origami-input-number': true,
        'origami-input-number-disabled': disabled ? true : false,
        [ sizeClassName ]: true,
        'origami-input-number-error': errorClassName,
    } );

    /**
     * Handles increment/decrement button clicks
     * 
     * @param {string} button - Button type ('up' or 'down')
     */
    const handleStep = ( button ) => {
        if ( button === 'up' &&  disableMax && number >= max ) {
            return;
        }
        const thisStep = step ? step : 1;
        let newValue = number || 0;
        switch ( button ) {
            case 'up':
                newValue = parseInt( newValue ) + parseInt( thisStep );
                setNumber( newValue );

                if ( typeof onStep === 'function' ) {
                    onStep( newValue, { type: button, offset: thisStep } );
                }
                break;
            case 'down':
                if ( parseInt( newValue ) <= 0 && preventNegative ) {
                    setNumber( 0 );
                    return;
                }
                newValue = parseInt( newValue ) - parseInt( thisStep );
                setNumber( newValue );

                if ( typeof onStep === 'function' ) {
                    onStep( newValue, { type: button, offset: thisStep } );
                }
                break;
        }

    };

    /**
     * Handles input value changes
     * 
     * @param {number} e - New input value
     */
    const handleInputChange = ( e ) => {
        if ( preventNegative && e < 0 ) {
            e = 0;
        }
        setNumber( e );
        if ( typeof onChange === 'function' ) {
            onChange( e );
        }
    };

    return (
        <div className={ inputClass }>
            <Button
                className='subtract-btn'
                aria-label="subtract button"
                icon={ <MathMinus /> }
                onClick={ ( e ) => handleStep( 'down' ) }
                size={ size }
                disabled={ disabled }
            ></Button>

            <AntInputNumber
                className="nkl-input"
                onKeyUp={ onKeyUp }
                value={ number }
                onChange={ ( e ) => handleInputChange( e ) }
                changeOnBlur={ true }
                type={ 'number' }
                ref={ ref }
                disabled={ disabled }
                addonAfter={ ( error && !( number === 0 && optional === true ) ) && <ErrorFilled /> }
                placeholder={ 0 }
                { ...restProps }
                controls={ false }
            />

            <Button
                className='add-btn'
                aria-label="Add button"
                icon={ <MathPlus /> }
                onClick={ ( e ) => handleStep( 'up' ) }
                size={ size }
                disabled={ disabled || ( disableMax && number >= max ) }
            ></Button>
        </div>
    );
} );

export { InputNumber };
